import {
  Button,
  Checkbox,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";

export interface CopyFindingsTarget {
  id: string;
  label: string;
  hasFindings: boolean;
}

/**
 * Copies one note's findings into other notes of the same signing. Each note keeps its own text
 * afterwards, because each note's PDF prints its own findings.
 */
export default function CopyFindingsPopover(props: {
  targets: CopyFindingsTarget[];
  onApply: (targetIds: string[]) => void;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  // Notes that already have findings start unchecked, so the default never overwrites text.
  const handleOpen = () => {
    setSelected(
      new Set(props.targets.filter((target) => !target.hasFindings).map((target) => target.id))
    );
    onOpen();
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const overwriteCount = props.targets.filter(
    (target) => target.hasFindings && selected.has(target.id)
  ).length;

  const handleApply = () => {
    props.onApply(props.targets.filter((target) => selected.has(target.id)).map((t) => t.id));
    onClose();
  };

  return (
    <Popover
      isLazy={true}
      isOpen={isOpen}
      placement="bottom-start"
      onClose={onClose}
      onOpen={handleOpen}
    >
      <PopoverTrigger>
        <Button size="xs" variant="link">
          Copy to other notes…
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverHeader fontSize="sm" fontWeight="semibold">
          Copy these findings to
        </PopoverHeader>
        <PopoverBody>
          <Flex gap={3} mb={2}>
            <Button
              size="xs"
              variant="link"
              onClick={() => setSelected(new Set(props.targets.map((target) => target.id)))}
            >
              Select all
            </Button>
            <Button size="xs" variant="link" onClick={() => setSelected(new Set())}>
              Select none
            </Button>
          </Flex>
          <Stack maxH="240px" overflowY="auto" spacing={1}>
            {props.targets.map((target) => (
              <Checkbox
                key={target.id}
                isChecked={selected.has(target.id)}
                onChange={() => toggle(target.id)}
              >
                <Text as="span" fontSize="sm">
                  {target.label}
                </Text>
                {target.hasFindings ? (
                  <Text as="span" color="gray.500" fontSize="xs" ml={2}>
                    has findings
                  </Text>
                ) : null}
              </Checkbox>
            ))}
          </Stack>
          {overwriteCount === 0 ? null : (
            <Text color="orange.600" fontSize="xs" mt={2}>
              Replaces the findings already written for {overwriteCount} note
              {overwriteCount === 1 ? "" : "s"}.
            </Text>
          )}
        </PopoverBody>
        <PopoverFooter display="flex" gap={2} justifyContent="flex-end">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={selected.size === 0}
            size="sm"
            onClick={handleApply}
          >
            Apply
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}
